/**
 * The web editors' shared chrome (plan 7.3, 7.8): the save state in the
 * panel header (a polite live region), the findings banner with Fix with
 * Tabbio, and the small dialogs of the element bar (Edit link, Alt text,
 * Replace image). The dialogs are `WebDialog`s (a bottom sheet at phone
 * widths) that keep focus inside and give it back when they close.
 */
import { useId, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { PageFinding } from '@tabbio/types';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SpinnerGlyph } from '../../common/SpinnerGlyph';
import { WebDialog } from '../../common/WebDialog.web';
import { listArtifacts, mintPreviewUrl } from '../../../lib/artifacts-api';
import { useArtifactSignedUrl } from '../../../hooks/useArtifact';
import { isAllowedImageUrl, isAllowedLinkHref } from './page-editor-session';
import { saveStateView, type EditorSaveState } from './save-state';

/** "Saving…", "Saved", "Couldn't save. Try again." or the server's words, with Try again or Reload. */
export function SaveStatus({
  state,
  onRetry,
  onReload,
}: {
  state: EditorSaveState;
  onRetry: () => void;
  onReload: () => void;
}) {
  const { t } = useTranslation('common');
  const view = saveStateView(state);
  const words = view.text ?? (view.key ? t(view.key) : '');
  return (
    <span className={`tabbio-save-status${view.tone === 'error' ? ' is-error' : ''}`}>
      <span className="tabbio-save-status-words" role="status" aria-live="polite">
        {view.glyph === 'spinner' ? <SpinnerGlyph color="var(--text-secondary)" size={14} /> : null}
        {view.glyph === 'saved' ? <MaterialSymbol name="cloud_done" size={18} /> : null}
        {view.glyph === 'error' ? <MaterialSymbol name="error" size={18} /> : null}
        {words ? <span className="tabbio-save-status-text">{words}</span> : null}
      </span>
      {view.action === 'retry' ? (
        <button type="button" className="tabbio-text-button" onClick={onRetry}>
          {t('chat.artifacts.edit.retry')}
        </button>
      ) : null}
      {view.action === 'reload' ? (
        <button type="button" className="tabbio-text-button" onClick={onReload}>
          {t('chat.artifacts.edit.reload')}
        </button>
      ) : null}
    </span>
  );
}

/**
 * The checks' findings (plan 7.3): errors block publishing, never the save;
 * P1 advice is counted beside them. Fix with Tabbio asks the agent with the
 * file as context.
 */
export function FindingsBanner({
  findings,
  warnings,
  errorCount,
  warningCount,
  removed,
  onFix,
}: {
  findings: readonly PageFinding[];
  warnings: readonly PageFinding[];
  errorCount: number;
  warningCount: number;
  removed?: readonly string[];
  onFix: () => void;
}) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const listId = useId();
  const showRemoved = Boolean(removed?.length);
  if (errorCount <= 0 && warningCount <= 0 && !showRemoved) return null;
  const items = [...findings, ...warnings];
  return (
    <div className={`tabbio-findings-banner${errorCount > 0 ? ' is-blocking' : ''}`} role="region" aria-label={t('chat.artifacts.edit.fixWithTabbio')}>
      {showRemoved ? (
        <div className="tabbio-findings-line">
          <MaterialSymbol name="info" size={18} />
          <span>{t('chat.artifacts.edit.removedNote')}</span>
        </div>
      ) : null}
      {errorCount > 0 || warningCount > 0 ? (
        <div className="tabbio-findings-line">
          <MaterialSymbol name={errorCount > 0 ? 'error' : 'lightbulb'} size={18} />
          <span className="tabbio-findings-count">
            {[
              errorCount > 0 ? t('chat.artifacts.edit.findings', { count: errorCount }) : null,
              warningCount > 0 ? t('chat.artifacts.edit.advice', { count: warningCount }) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          {items.length ? (
            <button
              type="button"
              className="tabbio-text-button"
              aria-expanded={open}
              aria-controls={listId}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? t('chat.artifacts.edit.hideFindings') : t('chat.artifacts.edit.showFindings')}
            </button>
          ) : null}
          <button type="button" className="tabbio-findings-fix" onClick={onFix}>
            <MaterialSymbol name="auto_awesome" size={16} />
            {t('chat.artifacts.edit.fixWithTabbio')}
          </button>
        </div>
      ) : null}
      {open && items.length ? (
        <ul id={listId} className="tabbio-findings-list">
          {items.map((finding, index) => (
            <li key={`${finding.ruleId}-${index}`} dir="auto">
              {finding.message}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

// ── Dialogs of the element bar ──────────────────────────────────────────────

export function EditLinkDialog({
  href,
  text,
  onSave,
  onClose,
}: {
  href: string;
  text: string;
  onSave: (href: string, text: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [address, setAddress] = useState(href);
  const [words, setWords] = useState(text);
  const [invalid, setInvalid] = useState(false);
  const errorId = useId();
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!isAllowedLinkHref(address)) {
      setInvalid(true);
      return;
    }
    onSave(address.trim(), words);
  };
  return (
    <WebDialog title={t('chat.artifacts.link.title')} onClose={onClose} maxWidth={440} zIndex={1150}>
      <form className="tabbio-editor-form" onSubmit={submit}>
        <label className="tabbio-editor-field">
          <span>{t('chat.artifacts.link.url')}</span>
          <input
            dir="ltr"
            type="url"
            inputMode="url"
            autoFocus
            value={address}
            onChange={(event) => {
              setAddress(event.target.value);
              setInvalid(false);
            }}
            aria-invalid={invalid || undefined}
            aria-describedby={errorId}
          />
          <small id={errorId} className={invalid ? 'is-error' : undefined}>
            {invalid ? t('chat.artifacts.link.invalid') : t('chat.artifacts.link.urlHint')}
          </small>
        </label>
        <label className="tabbio-editor-field">
          <span>{t('chat.artifacts.link.text')}</span>
          <input dir="auto" value={words} onChange={(event) => setWords(event.target.value)} />
        </label>
        <div className="tabbio-editor-form-actions">
          <button type="button" className="tabbio-text-button" onClick={onClose}>
            {t('chat.artifacts.link.cancel')}
          </button>
          <button type="submit" className="tabbio-panel-primary is-small">
            {t('chat.artifacts.link.save')}
          </button>
        </div>
      </form>
    </WebDialog>
  );
}

export function AltTextDialog({
  alt,
  onSave,
  onClose,
}: {
  alt: string;
  onSave: (alt: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const [value, setValue] = useState(alt);
  return (
    <WebDialog title={t('chat.artifacts.image.altTitle')} onClose={onClose} maxWidth={440} zIndex={1150}>
      <form
        className="tabbio-editor-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSave(value);
        }}
      >
        <label className="tabbio-editor-field">
          <span>{t('chat.artifacts.image.altLabel')}</span>
          <textarea dir="auto" rows={3} maxLength={300} autoFocus value={value} onChange={(event) => setValue(event.target.value)} />
        </label>
        <div className="tabbio-editor-form-actions">
          <button type="button" className="tabbio-text-button" onClick={onClose}>
            {t('chat.artifacts.image.cancel')}
          </button>
          <button type="submit" className="tabbio-panel-primary is-small">
            {t('chat.artifacts.image.save')}
          </button>
        </div>
      </form>
    </WebDialog>
  );
}

export type ReplaceImageChoice =
  | { type: 'artifact'; artifactId: string; displaySrc: string; title: string }
  | { type: 'url'; url: string };

/** Replace image: one of the user's ready images, or an https link. */
export function ReplaceImageDialog({
  onChoose,
  onClose,
}: {
  onChoose: (choice: ReplaceImageChoice) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const images = useQuery({
    queryKey: ['artifacts', 'list', 'images-ready'],
    queryFn: () => listArtifacts({ kind: 'image', status: 'ready', limit: 30 }),
    staleTime: 30_000,
  });
  const [url, setUrl] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const errorId = useId();
  const list = useMemo(() => images.data?.artifacts ?? [], [images.data]);

  const choose = async (artifactId: string, title: string) => {
    if (busy) return;
    setBusy(artifactId);
    try {
      // The frame needs a link it can load; the page stores the reference.
      const signed = await mintPreviewUrl(artifactId, 'image');
      onChoose({ type: 'artifact', artifactId, displaySrc: signed.url, title });
    } catch {
      setBusy(null);
    }
  };

  return (
    <WebDialog title={t('chat.artifacts.image.replaceTitle')} onClose={onClose} maxWidth={520} zIndex={1150}>
      <div className="tabbio-editor-form">
        <div className="tabbio-editor-field">
          <span>{t('chat.artifacts.image.yours')}</span>
          {images.isLoading ? (
            <div className="tabbio-image-picker-note" role="status">
              <SpinnerGlyph color="var(--text-secondary)" size={16} />
              {t('chat.artifacts.image.loadingYours')}
            </div>
          ) : list.length === 0 ? (
            <div className="tabbio-image-picker-note">{t('chat.artifacts.image.noImages')}</div>
          ) : (
            <div className="tabbio-image-picker" role="list">
              {list.map((item) => (
                <ImageChoice
                  key={item.id}
                  artifactId={item.id}
                  title={item.title}
                  busy={busy === item.id}
                  onChoose={() => void choose(item.id, item.title)}
                />
              ))}
            </div>
          )}
        </div>
        <form
          className="tabbio-editor-field"
          onSubmit={(event) => {
            event.preventDefault();
            if (!isAllowedImageUrl(url)) {
              setInvalid(true);
              return;
            }
            onChoose({ type: 'url', url: url.trim() });
          }}
        >
          <label htmlFor={`${errorId}-url`}>{t('chat.artifacts.image.urlLabel')}</label>
          <div className="tabbio-editor-inline">
            <input
              id={`${errorId}-url`}
              dir="ltr"
              type="url"
              inputMode="url"
              placeholder="https://"
              value={url}
              onChange={(event) => {
                setUrl(event.target.value);
                setInvalid(false);
              }}
              aria-invalid={invalid || undefined}
              aria-describedby={errorId}
            />
            <button type="submit" className="tabbio-panel-primary is-small" disabled={!url.trim()}>
              {t('chat.artifacts.image.use')}
            </button>
          </div>
          <small id={errorId} className={invalid ? 'is-error' : undefined}>
            {invalid ? t('chat.artifacts.image.urlInvalid') : t('chat.artifacts.image.urlHint')}
          </small>
        </form>
      </div>
    </WebDialog>
  );
}

function ImageChoice({
  artifactId,
  title,
  busy,
  onChoose,
}: {
  artifactId: string;
  title: string;
  busy: boolean;
  onChoose: () => void;
}) {
  const { t } = useTranslation('common');
  const thumb = useArtifactSignedUrl(artifactId, 'thumbnail', true);
  return (
    <button
      type="button"
      role="listitem"
      className="tabbio-image-choice"
      onClick={onChoose}
      aria-label={t('chat.artifacts.image.choose', { title: title || t('chat.artifacts.kind.image') })}
      aria-busy={busy || undefined}
    >
      {thumb.url ? <img src={thumb.url} alt="" referrerPolicy="no-referrer" onError={thumb.reportLoadError} /> : null}
      {busy ? (
        <span className="tabbio-image-choice-busy">
          <SpinnerGlyph color="var(--text-primary)" size={18} />
        </span>
      ) : null}
    </button>
  );
}
